import * as pc from 'playcanvas'
import {
  GSplatOctreeResource as ProductionGSplatOctreeResource
} from 'playcanvas/build/playcanvas/src/scene/gsplat-unified/gsplat-octree.resource.js'

let remoteSessionId = 1

const normalizePath = value => {
  const normalized = String(value || '').replace(/^(?:\.\/)+/, '').replace(/\/+$/, '')
  if (!normalized || normalized.startsWith('/') || normalized.includes('\\') || normalized.includes('\0')) return ''
  const parts = normalized.split('/')
  if (parts.some(part => !part || part === '.' || part === '..')) return ''
  return parts.join('/')
}

const getDirectory = value => {
  const normalized = normalizePath(value)
  const index = normalized.lastIndexOf('/')
  return index >= 0 ? normalized.slice(0, index) : ''
}

const joinPath = (base, relative) => {
  const stack = normalizePath(base).split('/').filter(Boolean)
  for (const part of String(relative || '').replace(/\\/g, '/').split('/')) {
    if (!part || part === '.') continue
    if (part === '..') stack.pop()
    else stack.push(part)
  }
  return stack.join('/')
}

const mimeForPath = entryPath => {
  if (/\.json$/i.test(entryPath)) return 'application/json'
  if (/\.webp$/i.test(entryPath)) return 'image/webp'
  return 'application/octet-stream'
}

const remoteVirtualUrl = (sessionId, entryPath) => (
  `remote-eos-playcanvas-lod://${sessionId}/${normalizePath(entryPath)}`
)

const remoteVirtualPath = url => normalizePath(
  url.replace(/^remote-eos-playcanvas-lod:\/\/\d+\//, '')
)

const createHttpError = (message, status = 0) => {
  const error = new Error(message)
  error.status = status
  return error
}

const fetchManifestJson = async manifestUrl => {
  const response = await fetch(manifestUrl, {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  })
  if (!response.ok) throw createHttpError('Unable to load the PlayCanvas LOD manifest', response.status)
  return response.json()
}

const normalizeEosDescriptor = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  if (value.format !== 'playcanvas-lod-eos-v1') return null

  const archiveSize = Number(value.archive_size)
  const lodMetaPath = normalizePath(String(value.lod_meta_path || ''))
  if (value.version !== 1 || !String(value.archive_url || '').trim() ||
    !Number.isSafeInteger(archiveSize) || archiveSize <= 0 ||
    !lodMetaPath || !value.entries || typeof value.entries !== 'object' || Array.isArray(value.entries)) {
    throw new Error('EOS returned an invalid PlayCanvas LOD descriptor')
  }

  const entries = Object.create(null)
  for (const [rawName, rawEntry] of Object.entries(value.entries)) {
    const name = normalizePath(rawName)
    const archivePath = normalizePath(String(rawEntry?.archive_path || ''))
    const offset = Number(rawEntry?.offset)
    const size = Number(rawEntry?.size)
    const mime = String(rawEntry?.mime || mimeForPath(name)).trim().toLowerCase().split(';', 1)[0]
    if (!name || !archivePath || !Number.isSafeInteger(offset) || offset < 0 ||
      !Number.isSafeInteger(size) || size <= 0 || offset + size > archiveSize ||
      !/^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(mime)) {
      throw new Error('EOS returned an invalid PlayCanvas LOD entry range')
    }
    entries[name] = { archive_path: archivePath, offset, size, mime }
  }
  if (!entries[lodMetaPath]) throw new Error('EOS PlayCanvas LOD descriptor is missing lod-meta.json')

  return {
    format: 'playcanvas-lod-eos-v1',
    version: 1,
    archive_url: String(value.archive_url),
    archive_size: archiveSize,
    lod_meta_path: lodMetaPath,
    lod_levels: Number(value.lod_levels || 0),
    node_count: Number(value.node_count || 0),
    entries
  }
}

const parseJsonBlob = async blob => {
  const text = new TextDecoder('utf-8', { fatal: true }).decode(await blob.arrayBuffer())
  return JSON.parse(text)
}

const collectSogTexturePaths = (metaPath, value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`EOS LOD node metadata is invalid: ${metaPath}`)
  }
  const filenames = []
  for (const sectionName of ['means', 'scales', 'quats', 'sh0', 'shN']) {
    const section = value[sectionName]
    if (!section || typeof section !== 'object' || Array.isArray(section)) continue
    const files = section.files
    if (!Array.isArray(files)) continue
    for (const filename of files) {
      if (typeof filename !== 'string') throw new Error(`EOS LOD node metadata is invalid: ${metaPath}`)
      const resolved = normalizePath(joinPath(getDirectory(metaPath), filename))
      if (!resolved) throw new Error(`EOS LOD node contains an unsafe texture path: ${metaPath}`)
      filenames.push(resolved)
    }
  }
  return [...new Set(filenames)]
}

const loadGSplatOctreeResourceConstructor = async () => {
  // Keep both import specifiers literal so Vite can transform the bare package paths in dev mode.
  if (import.meta.env.DEV) {
    const module = await import(
      'playcanvas/build/playcanvas.dbg/src/scene/gsplat-unified/gsplat-octree.resource.js'
    )
    return module.GSplatOctreeResource
  }
  return ProductionGSplatOctreeResource
}

class EosRangeSession {
  constructor(manifestUrl, descriptor) {
    this.id = remoteSessionId++
    this.manifestUrl = manifestUrl
    this.descriptor = descriptor
    this.refreshPromise = null
    this.objectUrls = new Map()
    this.objectUrlPromises = new Map()
    this.requests = new Set()
    this.destroyed = false
  }

  get lodMetaPath() {
    return this.descriptor.lod_meta_path
  }

  async refreshDescriptor() {
    if (this.refreshPromise) return this.refreshPromise
    this.refreshPromise = (async () => {
      const refreshed = normalizeEosDescriptor(await fetchManifestJson(this.manifestUrl))
      if (!refreshed || refreshed.archive_size !== this.descriptor.archive_size ||
        refreshed.lod_meta_path !== this.descriptor.lod_meta_path) {
        throw new Error('EOS PlayCanvas LOD descriptor changed while it was being loaded')
      }
      this.descriptor = refreshed
      return refreshed
    })()
    try {
      return await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  resolveEntry(basePath, filename) {
    const direct = normalizePath(filename)
    if (direct && this.descriptor.entries[direct]) return direct
    const relative = normalizePath(joinPath(getDirectory(basePath), filename))
    if (relative && this.descriptor.entries[relative]) return relative
    throw new Error(`EOS LOD archive is missing an entry: ${filename}`)
  }

  async fetchRange(entryPath, allowRefresh) {
    if (this.destroyed) throw new Error('EOS LOD session was destroyed')
    const normalized = normalizePath(entryPath)
    const entry = this.descriptor.entries[normalized]
    if (!entry) throw new Error(`EOS LOD archive is missing an entry: ${entryPath}`)

    const controller = new AbortController()
    this.requests.add(controller)
    let response
    try {
      response = await fetch(this.descriptor.archive_url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
        headers: { Range: `bytes=${entry.offset}-${entry.offset + entry.size - 1}` },
        signal: controller.signal
      })
    } finally {
      this.requests.delete(controller)
    }

    if ([401, 403].includes(response.status) && allowRefresh) {
      await this.refreshDescriptor()
      return this.fetchRange(normalized, false)
    }
    if (response.status !== 206) {
      throw createHttpError('EOS did not honor the PlayCanvas LOD byte-range request', response.status)
    }

    const refreshedEntry = this.descriptor.entries[normalized]
    if (!refreshedEntry || refreshedEntry.offset !== entry.offset || refreshedEntry.size !== entry.size) {
      throw new Error('EOS PlayCanvas LOD entry changed while it was being loaded')
    }
    const contentRange = response.headers.get('Content-Range')
    if (contentRange) {
      const match = /^bytes (\d+)-(\d+)\/(\d+|\*)$/i.exec(contentRange.trim())
      if (!match || Number(match[1]) !== entry.offset || Number(match[2]) !== entry.offset + entry.size - 1 ||
        (match[3] !== '*' && Number(match[3]) !== this.descriptor.archive_size)) {
        throw new Error('EOS returned a mismatched PlayCanvas LOD byte range')
      }
    }
    const blob = await response.blob()
    if (blob.size !== entry.size) throw new Error('EOS returned an incomplete PlayCanvas LOD entry')
    return blob.slice(0, blob.size, entry.mime)
  }

  async fetchEntry(entryPath) {
    return this.fetchRange(entryPath, true)
  }

  async acquireObjectUrl(entryPath, sourceBlob) {
    const normalized = normalizePath(entryPath)
    const existing = this.objectUrls.get(normalized)
    if (existing) {
      existing.refs += 1
      return existing.url
    }

    let pending = this.objectUrlPromises.get(normalized)
    if (!pending) {
      pending = (async () => {
        const blob = sourceBlob || await this.fetchEntry(normalized)
        const record = { url: URL.createObjectURL(blob), refs: 0 }
        this.objectUrls.set(normalized, record)
        return record
      })()
      this.objectUrlPromises.set(normalized, pending)
    }
    try {
      const record = await pending
      record.refs += 1
      return record.url
    } finally {
      this.objectUrlPromises.delete(normalized)
    }
  }

  releaseObjectUrls(entryPaths) {
    for (const entryPath of new Set(entryPaths)) {
      const normalized = normalizePath(entryPath)
      const record = this.objectUrls.get(normalized)
      if (!record) continue
      record.refs -= 1
      if (record.refs <= 0) {
        URL.revokeObjectURL(record.url)
        this.objectUrls.delete(normalized)
      }
    }
  }

  destroy() {
    if (this.destroyed) return
    this.destroyed = true
    this.requests.forEach(request => request.abort())
    this.requests.clear()
    this.objectUrls.forEach(record => URL.revokeObjectURL(record.url))
    this.objectUrls.clear()
    this.objectUrlPromises.clear()
  }
}

class EosRangeGsplatLoader {
  constructor(registry, session) {
    this.registry = registry
    this.session = session
    this.assets = new Map()
    this.loading = new Set()
    this.queued = []
    this.loadTokens = new Map()
    this.failed = new Set()
    this.destroyed = false
    this.maxConcurrentLoads = 2
  }

  load(url) {
    if (this.destroyed || !this.registry || this.assets.get(url)?.asset.loaded ||
      this.loading.has(url) || this.queued.includes(url) || this.failed.has(url)) return
    if (this.loading.size >= this.maxConcurrentLoads) this.queued.push(url)
    else this.start(url)
  }

  start(url) {
    this.loading.add(url)
    const token = this.loadTokens.get(url) || 0
    void this.loadBundle(url, token).then(record => {
      if (record) this.assets.set(url, record)
    }).catch(() => {
      this.failed.add(url)
    }).finally(() => {
      this.loading.delete(url)
      this.processQueue()
    })
  }

  async loadBundle(url, token) {
    const registry = this.registry
    if (!registry || this.destroyed) return null
    const metaPath = remoteVirtualPath(url)
    const acquired = []
    let asset = null

    try {
      const metaBlob = await this.session.fetchEntry(metaPath)
      const meta = await parseJsonBlob(metaBlob)
      const texturePaths = collectSogTexturePaths(metaPath, meta)
      const metaObjectUrl = await this.session.acquireObjectUrl(metaPath, metaBlob)
      acquired.push(metaPath)
      const mappedUrls = new Map()
      await Promise.all(texturePaths.map(async entryPath => {
        const objectUrl = await this.session.acquireObjectUrl(entryPath)
        acquired.push(entryPath)
        mappedUrls.set(entryPath, objectUrl)
      }))

      if (this.destroyed || token !== (this.loadTokens.get(url) || 0) || !this.registry) {
        this.session.releaseObjectUrls(acquired)
        return null
      }

      asset = new pc.Asset(url, 'gsplat', {
        url: metaObjectUrl,
        filename: metaPath
      }, {}, {
        minimalMemory: true,
        mapUrl: filename => mappedUrls.get(this.session.resolveEntry(metaPath, filename)) || filename
      })
      await new Promise((resolve, reject) => {
        asset.once('load', () => resolve())
        asset.once('error', error => reject(error))
        this.registry?.add(asset)
        this.registry?.load(asset)
      })

      if (this.destroyed || token !== (this.loadTokens.get(url) || 0) || !this.registry) {
        registry.remove(asset)
        asset.unload()
        this.session.releaseObjectUrls(acquired)
        return null
      }
      return { asset, entryPaths: acquired }
    } catch (error) {
      if (asset) registry.remove(asset)
      asset?.unload()
      this.session.releaseObjectUrls(acquired)
      throw error
    }
  }

  processQueue() {
    while (!this.destroyed && this.loading.size < this.maxConcurrentLoads && this.queued.length) {
      const url = this.queued.shift()
      if (url) this.start(url)
    }
  }

  unload(url) {
    this.loadTokens.set(url, (this.loadTokens.get(url) || 0) + 1)
    this.failed.delete(url)
    const queueIndex = this.queued.indexOf(url)
    if (queueIndex >= 0) this.queued.splice(queueIndex, 1)
    const record = this.assets.get(url)
    if (!record || !this.registry) return
    record.asset.fire('unload', record.asset)
    this.registry.remove(record.asset)
    record.asset.unload()
    this.session.releaseObjectUrls(record.entryPaths)
    this.assets.delete(url)
    this.processQueue()
  }

  getResource(url) {
    return this.assets.get(url)?.asset.resource
  }

  destroy() {
    if (this.destroyed) return
    this.destroyed = true
    this.queued.length = 0
    for (const [url, record] of this.assets) {
      record.asset.fire('unload', record.asset)
      this.registry?.remove(record.asset)
      record.asset.unload()
      this.session.releaseObjectUrls(record.entryPaths)
      this.assets.delete(url)
    }
    this.loading.clear()
    this.registry = null
    this.session.destroy()
  }
}

const loadEosPlayCanvasLod = async (registry, manifestUrl, descriptor) => {
  const session = new EosRangeSession(manifestUrl, descriptor)
  try {
    const lodMetaBlob = await session.fetchEntry(session.lodMetaPath)
    const lodMeta = await parseJsonBlob(lodMetaBlob)
    if (!lodMeta || !Number.isSafeInteger(lodMeta.lodLevels) || lodMeta.lodLevels < 1 ||
      !Array.isArray(lodMeta.filenames) || !lodMeta.filenames.length ||
      !Array.isArray(lodMeta.tree?.bound?.min) || !Array.isArray(lodMeta.tree?.bound?.max)) {
      throw new Error('EOS lod-meta.json is not a valid PlayCanvas LOD octree')
    }

    const GSplatOctreeResource = await loadGSplatOctreeResourceConstructor()
    const metaUrl = remoteVirtualUrl(session.id, session.lodMetaPath)
    const loader = new EosRangeGsplatLoader(registry, session)
    const asset = new pc.Asset('lod-meta.json', 'gsplat', { url: metaUrl, filename: 'lod-meta.json' })
    const resource = new GSplatOctreeResource(metaUrl, lodMeta, loader)
    lodMeta.filenames.forEach((filename, index) => {
      resource.octree.files[index].url = remoteVirtualUrl(
        session.id,
        session.resolveEntry(session.lodMetaPath, filename)
      )
    })
    if (typeof lodMeta.environment === 'string') {
      resource.octree.environmentUrl = remoteVirtualUrl(
        session.id,
        session.resolveEntry(session.lodMetaPath, lodMeta.environment)
      )
    }
    asset.resource = resource
    asset.loaded = true
    registry.add(asset)
    return asset
  } catch (error) {
    session.destroy()
    throw error
  }
}

const loadLegacyRemotePlayCanvasLod = (registry, manifestUrl) => {
  const asset = new pc.Asset('lod-meta.json', 'gsplat', {
    url: manifestUrl,
    filename: 'lod-meta.json'
  })
  return new Promise((resolve, reject) => {
    asset.once('load', () => resolve(asset))
    asset.once('error', error => reject(error))
    registry.add(asset)
    registry.load(asset)
  })
}

/**
 * Detect the new EOS/OSS descriptor without changing callers. Existing lod-meta.json URLs and
 * endpoints that cannot be inspected continue through PlayCanvas' original remote LOD loader.
 */
export async function loadRemotePlayCanvasLod(registry, manifestUrl) {
  let manifest
  try {
    manifest = await fetchManifestJson(manifestUrl)
  } catch {
    return loadLegacyRemotePlayCanvasLod(registry, manifestUrl)
  }
  const eosDescriptor = normalizeEosDescriptor(manifest)
  return eosDescriptor
    ? loadEosPlayCanvasLod(registry, manifestUrl, eosDescriptor)
    : loadLegacyRemotePlayCanvasLod(registry, manifestUrl)
}
